import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Red de seguridad para errores de render.
 *
 * RECUPERADO de la version desplegada en junio (2026-08-19). La reconstruccion
 * se lo dejo por el camino, y sin el un error de render en cualquier
 * componente deja la pantalla EN BLANCO: React desmonta el arbol entero y no
 * pinta nada. El usuario ve una pagina vacia y no sabe si el problema es suyo,
 * de la conexion o nuestro.
 *
 * Es una clase y no un hook porque `getDerivedStateFromError` /
 * `componentDidCatch` no tienen equivalente en componentes de funcion; sigue
 * siendo la unica forma de capturar un error de render en React 19.
 *
 * El `fallback` opcional permite que una zona concreta se recupere sin tirar de
 * la pantalla entera. Reproduce la firma que tenia la version desplegada.
 */
interface Props {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info);
  }

  reset = (): void => this.setState({ error: null });

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(error, this.reset);

    return (
      <div className="min-h-[60vh] w-full flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center">
          <AlertTriangle className="w-10 h-10 text-destructive mx-auto mb-4" />
          <h1 className="text-xl font-semibold mb-2">Something went wrong</h1>
          <p className="text-sm text-muted-foreground mb-6">
            {error.message || "An unexpected error occurred."}
          </p>
          <div className="flex gap-2 justify-center">
            <Button variant="outline" onClick={this.reset}>
              Try again
            </Button>
            <Button onClick={() => window.location.assign("/")}>Go home</Button>
          </div>
        </div>
      </div>
    );
  }
}
